import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  ViewChild,
} from '@angular/core';

import { FormlyConfig } from '@ngx-formly/core';
import { Observable, firstValueFrom, map } from 'rxjs';

import {
  DYNAMIC_FORM_FOOTER_SELECTOR,
  DYNAMIC_FORM_HEADER_SELECTOR,
  DYNAMIC_FORM_SCROLLABLE_CONTAINER_SELECTOR,
} from 'common/dynamic-form/services/layout.service';

import { FieldHintComponent } from './hint/hint.component';

import { ISPFieldWrapperBase } from '../field-wrapper-base.class';
import { getErrorMessage$ } from '../validation-error/validation-error.utils';

/**
 * Base field wrapper component for Formly
 */
@Component({
  selector: 'isp-formly-field-base',
  templateUrl: './field-base.component.html',
  styleUrls: ['./scss/field.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldBaseComponent extends ISPFieldWrapperBase {
  private get haveErrorMessageToDisplay$(): Observable<boolean> {
    return getErrorMessage$(this.field, this.formlyConfig).pipe(
      map(message => Boolean(message)),
    );
  }

  private get haveHintMessageToDisplay(): Promise<boolean> {
    return firstValueFrom(
      this.props.hint$!.pipe(map(message => Boolean(message))),
    );
  }

  private get isForceHint(): boolean {
    return this.options.formState.context.forceHint;
  }

  get hintAttachContainer(): string {
    switch (this.props.layoutPlace) {
      case 'header':
        return DYNAMIC_FORM_HEADER_SELECTOR;
      case 'footer':
        return DYNAMIC_FORM_FOOTER_SELECTOR;
      default:
        return DYNAMIC_FORM_SCROLLABLE_CONTAINER_SELECTOR;
    }
  }

  get isErrorDisplayed$(): Observable<boolean> {
    return this.haveErrorMessageToDisplay$.pipe(
      map(message => message && this.showError),
    );
  }

  @ViewChild(FieldHintComponent) tooltip?: FieldHintComponent;

  constructor(private readonly formlyConfig: FormlyConfig) {
    super();
  }

  @HostListener('change', ['$event']) async onChange(
    event: InputEvent,
  ): Promise<void> {
    const haveHintMessageToDisplay = await this.haveHintMessageToDisplay;

    if (
      this.isForceHint &&
      haveHintMessageToDisplay &&
      !this.props.isHidden &&
      !this.showError &&
      event.isTrusted
    ) {
      this.tooltip?.toggle(true);
    }
  }

  @HostListener('focusin', ['$event']) async onFocusIn(
    event: FocusEvent,
  ): Promise<void> {
    const haveHintMessageToDisplay = await this.haveHintMessageToDisplay;

    if (
      (event.target as HTMLInputElement).type !== 'range' &&
      this.isForceHint &&
      haveHintMessageToDisplay &&
      !this.props.isHidden &&
      !this.showError
    ) {
      this.tooltip?.toggle(true);
    }
  }

  @HostListener('focusout') onBlur(): void {
    if (this.isForceHint) {
      this.tooltip?.toggle(false);
    }
  }
}
